import {
  DEFAULT_FEED_GROUP_BY,
  type FeedGroupBy,
} from "@/features/feed/feedDisplayGroups";
import {
  DEFAULT_HOME_METRICS_PERIOD,
  isHomeMetricsPeriod,
  type HomeMetricsPeriod,
} from "@/features/dashboard/home/homeDateRangePresets";
import type { HomeMetricsServiceTrade } from "@/features/dashboard/home/homeServiceTradePresets";

export const LEGACY_USER_PREFERENCES_STORAGE_KEY = "twido-v2-user-preferences";

export interface UserPreferences {
  /** Show UN/LOCODE alongside port names (e.g. "Shanghai (CNSHA)"). */
  showLocodes: boolean;
  /** Show carrier logos next to the always-visible carrier name. */
  showCarrierLogos: boolean;
  /** Show ISO week numbers next to dates (e.g. "Mar 14, 2026 · W11-2026"). */
  showWeekNumbers: boolean;
  /** Blur party names, emails, organization, and currency (privacy masking). */
  hideSensitiveData: boolean;
  /**
   * Scope bookings lists, home/report metric tiles, and related charts to the
   * current user's linked bookings (sales person OR Focus "Open By").
   * SalesPerson and Client accounts always stay scoped regardless of this flag.
   */
  scopeToMyBookings: boolean;
  /** How the full-page activity feed groups rows (default: by date). */
  feedGroupBy: FeedGroupBy;
  /** Entity section keys hidden from the feed via declutter (persisted). */
  feedHiddenGroups: string[];
  /** Play a short sound when new activity arrives while Twido is open. */
  notificationSounds: boolean;
  /**
   * Workspace-level period for the Home dashboard. Overlays a shared
   * date-range window onto eligible metric widgets at query time (never
   * persisted onto widget filters). See `homeDateRangePresets.ts`.
   */
  homeMetricsPeriod: HomeMetricsPeriod;
  /**
   * Workspace-level service trade for the Home dashboard. Overlays booking
   * filters and scopes booking-backed widgets at view time. `null` = all trades.
   */
  homeMetricsServiceTrade: HomeMetricsServiceTrade;
}

export function userPreferencesStorageKey(userSub: string): string {
  return `${LEGACY_USER_PREFERENCES_STORAGE_KEY}:${userSub}`;
}

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  showLocodes: true,
  showCarrierLogos: true,
  showWeekNumbers: true,
  hideSensitiveData: false,
  scopeToMyBookings: true,
  feedGroupBy: DEFAULT_FEED_GROUP_BY,
  feedHiddenGroups: [],
  notificationSounds: false,
  homeMetricsPeriod: DEFAULT_HOME_METRICS_PERIOD,
  homeMetricsServiceTrade: null,
};

function normalizePartial(
  parsed: Partial<UserPreferences> & { showCarrierNames?: boolean },
): UserPreferences {
  const { showCarrierNames: _legacy, ...rest } = parsed;
  const merged = { ...DEFAULT_USER_PREFERENCES, ...rest };
  if (!isHomeMetricsPeriod(merged.homeMetricsPeriod)) {
    merged.homeMetricsPeriod = DEFAULT_HOME_METRICS_PERIOD;
  }
  if (
    merged.homeMetricsServiceTrade != null &&
    typeof merged.homeMetricsServiceTrade !== "string"
  ) {
    merged.homeMetricsServiceTrade = null;
  }
  return merged;
}

function parseStored(raw: string): UserPreferences | null {
  try {
    return normalizePartial(
      JSON.parse(raw) as Partial<UserPreferences> & { showCarrierNames?: boolean },
    );
  } catch {
    return null;
  }
}

/** Load persisted preferences for an authenticated Auth0 `sub`. */
export function loadUserPreferencesForSub(userSub: string): UserPreferences {
  const keyedRaw = localStorage.getItem(userPreferencesStorageKey(userSub));
  if (keyedRaw) {
    const parsed = parseStored(keyedRaw);
    if (parsed) return parsed;
  }

  const legacyRaw = localStorage.getItem(LEGACY_USER_PREFERENCES_STORAGE_KEY);
  if (legacyRaw) {
    const parsed = parseStored(legacyRaw);
    if (parsed) {
      saveUserPreferencesForSub(userSub, parsed);
      localStorage.removeItem(LEGACY_USER_PREFERENCES_STORAGE_KEY);
      return parsed;
    }
  }

  return DEFAULT_USER_PREFERENCES;
}

export function saveUserPreferencesForSub(
  userSub: string,
  prefs: UserPreferences,
): void {
  localStorage.setItem(userPreferencesStorageKey(userSub), JSON.stringify(prefs));
}
